import ky, { Options as KyOptions } from "ky";
import { AjaxError } from "./ajax-error";
import { IHttpServiceClient } from "./ihttp-service-client";
import { IHttpServiceOptions } from "./ihttp-service-options";
import {
  BadRequestException,
  UnauthorizedException,
  ForbiddenException,
  ResourceNotFoundException,
  InternalServerException,
} from "./exceptions";
import { logout as authLogout } from '@/store';
import { tokenInterceptor } from '@/services/shared/http';

// Funzione di logout automatico
const autoLogout = async () => {
  await authLogout();
  window.location.href = '/';
};

export type IKyClientOptions = IHttpServiceOptions & Omit<KyOptions, 'headers'> & {
  headers?: HeadersInit;
};

export class KyClient implements IHttpServiceClient<IKyClientOptions> {
  public options: IKyClientOptions;
  private kyInstance: ReturnType<typeof ky.create>;

  constructor(options: IKyClientOptions) {
    this.options = this.convertHeaders(options);
    this.kyInstance = this.createKyInstance();
    
    console.log("Initialized KyClient with headers:", Object.fromEntries(this.options.headers as Headers));
  }

  private convertHeaders(options: IKyClientOptions): IKyClientOptions {
    const headers = new Headers(options.headers || {});

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return {
      ...options,
      headers,
    };
  }

  private createKyInstance() {
    return ky.create({
      ...this.options,
      timeout: 6000000,
      hooks: {
        beforeRequest: [
          (request) => {
            const token = tokenInterceptor.getToken();
            if (token) {
              request.headers.set('Authorization', `Bearer ${token}`);
            }
          },
        ],
        beforeError: [
          async (error) => {
            const { response } = error;
            if (response.status === 401) {
              await autoLogout(); // Richiama il logout automatico in caso di 401 Unauthorized
              return new UnauthorizedException(response, error.request, error.options);
            }
            // Gestione degli altri errori
            switch (response.status) {
              case 400:
                return new BadRequestException(response, error.request, error.options);
              case 403:
                return new ForbiddenException(response, error.request, error.options);
              case 404:
                return new ResourceNotFoundException(response, error.request, error.options);
              case 500:
                return new InternalServerException(response, error.request, error.options);
              default:
                return new AjaxError(
                  response.status,
                  response,
                  error.request,
                  error.options,
                  `Ajax error occurred (${response.status})`
                );
            }
          },
        ],
      },
    });
  }

  private logRequest(method: string, url: string, body?: unknown, options?: IKyClientOptions) {
    console.log(`Sending ${method} request to URL:`, this.options.prefixUrl + url);
    if (body) console.log("With body:", body);
    if (options) console.log("With headers:", Object.fromEntries(options.headers as Headers));
  }

  private handleResponse<R>(promise: Promise<R>): Promise<R> {
    return promise
      .then(response => {
        console.log("Server response:", response);
        return response;
      })
      .catch(error => {
        console.error("Error from server:", error);
        throw error;
      });
  }

  public get<R = unknown>(url: string, options?: IKyClientOptions): Promise<R> {
    const modifiedOptions = this.convertHeaders(options || {});
    this.logRequest('GET', url, null, modifiedOptions);
    return this.handleResponse(this.kyInstance.get(url, modifiedOptions).json());
  }

  public post<R = unknown, B = unknown>(
    url: string,
    body: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const modifiedOptions = this.convertHeaders(options || {});
    const relativeUrl = url.startsWith('/') ? url.slice(1) : url;
    this.logRequest('POST', relativeUrl, body, modifiedOptions);
    return this.handleResponse(this.kyInstance.post(url, { json: body, ...modifiedOptions }).json());
  }

  public put<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const modifiedOptions = this.convertHeaders(options || {});
    this.logRequest('PUT', url, body, modifiedOptions);
    return this.handleResponse(this.kyInstance.put(url, { json: body, ...modifiedOptions }).json());
  }

  public patch<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const modifiedOptions = this.convertHeaders(options || {});
    this.logRequest('PATCH', url, body, modifiedOptions);
    return this.handleResponse(this.kyInstance.patch(url, { json: body, ...modifiedOptions }).json());
  }

  public delete<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const modifiedOptions = this.convertHeaders(options || {});
    this.logRequest('DELETE', url, body, modifiedOptions);
    return this.handleResponse(this.kyInstance.delete(url, { json: body, ...modifiedOptions }).json());
  }
}
